import type { AuditLogEntryLocal } from '../api/types/TenantLocal';
import { SYSTEM_ACTOR } from './actorName';
import { auditTargetLabel, auditTargetTypeLabel } from './auditTargetLabels';
import { ORGANIZATION_USER, SELF_REGISTER_ACTOR_NAME, SYSTEM_ACTOR_NAME } from './labelConstants';

/** Actor que el back registra en los pasos del autorregistro. */
export const SELF_REGISTER_ACTOR = 'self_register';

/** Nombres a mano para rotular la auditoría de plataforma (id → nombre). */
export interface PlatformAuditNameMaps {
    admins: ReadonlyMap<string, string>;
    tenants: ReadonlyMap<string, string>;
    roles: ReadonlyMap<string, string>;
    applications: ReadonlyMap<string, string>;
    /** Llave `${applicationId}#${permissionKey}` → nombre del permiso. */
    permissions: ReadonlyMap<string, string>;
}

interface Snapshot {
    displayName?: unknown;
    cognitoSub?: unknown;
    roleId?: unknown;
}

function snapshotOf(row: AuditLogEntryLocal): Snapshot {
    const value = row.after ?? row.before;
    return value && typeof value === 'object' ? value : {};
}

function asText(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Id del padre en ids compuestos `padre#hijo`. */
function parentIdOf(id: string): string {
    const at = id.indexOf('#');
    return at < 0 ? id : id.slice(0, at);
}

/** Quién hizo la acción; un sub que no es de plataforma es un usuario de organización. */
export function platformAuditActorLabel(
    row: AuditLogEntryLocal,
    admins: ReadonlyMap<string, string>,
): string {
    const sub = row.actor?.cognitoSub;
    if (!sub) return '—';
    if (sub === SYSTEM_ACTOR) return SYSTEM_ACTOR_NAME;
    if (sub === SELF_REGISTER_ACTOR) return SELF_REGISTER_ACTOR_NAME;
    return admins.get(sub)?.trim() || asText(row.actor?.displayName) || ORGANIZATION_USER;
}

function roleAssignmentLabel(row: AuditLogEntryLocal, maps: PlatformAuditNameMaps): string {
    const snap = snapshotOf(row);
    const roleId = asText(snap.roleId);
    const sub = asText(snap.cognitoSub);
    const roleName = roleId ? maps.roles.get(roleId) : undefined;
    const who = sub ? maps.admins.get(sub) : undefined;
    const typeLabel = auditTargetTypeLabel('RoleAssignment');
    if (roleName && who) return `${typeLabel}: ${roleName} a ${who}`;
    if (roleName) return `${typeLabel}: ${roleName}`;
    return auditTargetLabel('RoleAssignment', row.target?.id ?? '');
}

function childOfLabel(
    type: string,
    id: string,
    childName: string | undefined,
    parentName: string | undefined,
): string {
    if (childName) return auditTargetLabel(type, id, childName);
    if (parentName) return `${auditTargetTypeLabel(type)} de ${parentName}`;
    return auditTargetLabel(type, id);
}

/** Recurso legible: nombre del mapa, del registro o del padre; el id corto solo como último recurso. */
export function platformAuditTargetLabel(
    row: AuditLogEntryLocal,
    maps: PlatformAuditNameMaps,
): string {
    const type = row.target?.type ?? '';
    const id = row.target?.id ?? '';
    if (type === 'RoleAssignment') return roleAssignmentLabel(row, maps);
    if (type === 'ApplicationPermission') {
        const appName = maps.applications.get(parentIdOf(id));
        return childOfLabel(type, id, maps.permissions.get(id), appName);
    }
    if (type === 'TenantUserField') {
        return childOfLabel(type, id, undefined, maps.tenants.get(parentIdOf(id)));
    }
    const byType: Partial<Record<string, ReadonlyMap<string, string>>> = {
        Tenant: maps.tenants,
        PlatformUser: maps.admins,
        TenantUser: maps.admins,
        Role: maps.roles,
        Application: maps.applications,
    };
    const name = byType[type]?.get(id) ?? asText(snapshotOf(row).displayName);
    return auditTargetLabel(type, id, name);
}
